/* Human-based face engine adapter */

import type { FaceBox, FaceDescriptor } from './face';
// Use loose typing to avoid tight coupling with Human TS types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let HumanCtor: any | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let human: any | null = null;
let humanLoaded = false;

const DEFAULT_HUMAN_MODELS = 'https://cdn.jsdelivr.net/npm/@vladmandic/human/models';

function l2norm(vec: number[]): number[] {
  let s = 0;
  for (let i = 0; i < vec.length; i++) { const v = vec[i] ?? 0; s += v * v; }
  const r = Math.sqrt(s) || 1;
  const out = new Array(vec.length);
  for (let i = 0; i < vec.length; i++) out[i] = (vec[i] ?? 0) / r;
  return out;
}

export async function ensureHumanLoaded(modelBaseUrl: string = DEFAULT_HUMAN_MODELS) {
  if (humanLoaded && human) return human;
  if (!HumanCtor) {
    // Dynamic import to keep bundle smaller when not used
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    HumanCtor = (await import('@vladmandic/human')).default as unknown as any;
  }
  // Minimal config focusing on detection + embeddings
  const cfg: any = {
    backend: 'webgl',
    modelBasePath: modelBaseUrl,
    cacheSensitivity: 0,
    debug: false,
    face: {
      enabled: true,
      detector: { rotation: true, maxDetected: 10, minConfidence: 0.6 },
      mesh: { enabled: false },
      iris: { enabled: false },
      attention: { enabled: false },
      description: { enabled: true }, // enables embeddings
      emotion: { enabled: false },
      antispoof: { enabled: false },
      liveness: { enabled: false },
    },
    body: { enabled: false },
    hand: { enabled: false },
    object: { enabled: false },
    gesture: { enabled: false },
  };
  human = new HumanCtor(cfg);
  await human.load();
  // Optional warmup for faster first inference
  try { await human.warmup(); } catch {}
  humanLoaded = true;
  return human;
}

export async function computeFaceDescriptorsFromBlob(blob: Blob, opts: { strict?: boolean } = {}): Promise<{ descriptors: FaceDescriptor[]; boxes: FaceBox[] }>{
  if (!human) await ensureHumanLoaded();
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });

    // Override minimal per-call settings
    const override: any = opts.strict
      ? { face: { detector: { minConfidence: 0.75 } } }
      : { face: { detector: { minConfidence: 0.6 } } };

    const res = await human!.detect(img, override);
    const faces: any[] = (res && res.face) ? res.face : [];

    const descriptors: FaceDescriptor[] = [];
    const boxes: FaceBox[] = [];

    for (const f of faces) {
      const emb: number[] | undefined = Array.isArray(f?.embedding) ? (f.embedding as number[]) : undefined;
      const box = f?.box || f?.face?.box;
      if (!emb || emb.length < 16 || !box) continue;
      // L2 normalize to make distances consistent
      const norm = l2norm(emb);
      descriptors.push(norm);
      boxes.push({ x: box.x, y: box.y, width: box.width, height: box.height });
    }

    return { descriptors, boxes };
  } finally {
    URL.revokeObjectURL(url);
  }
}
